import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, APP_INITIALIZER, inject } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { httpAuthInterceptor } from './interceptors/http-auth.interceptor';
import { GainsightAnalyticsService } from './services/analytics/gainsight-analytics.service';
import { AuraAnalyticsService } from './services/analytics/aura-analytics.service';
import { EnvironmentService } from './services/environment.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ApiModule, BASE_PATH, Configuration, ConfigurationParameters } from '../api';
import { EmbeddedAuthService } from './services/auth/embedded-auth.service';
import { StandardAuthService } from './services/auth/standard-auth.service';
import { AUTH_SERVICE_TOKEN } from './services/auth/auth.interface';
import { HostApplicationService } from './services/host-application.service';
import { AURA_UI_BASE_PATH, AURA_UI_CHAT_AUTH, provideAuraApi, provideAuraMarkdown } from '@powerfleet/aura_standalone.ui';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    {
      provide: APP_INITIALIZER,
      useFactory: (environmentService: EnvironmentService, hostApplicationService: HostApplicationService, gainsightAnalyticsService: GainsightAnalyticsService) => () => {
        environmentService.initialise();
        // Chat i18n is owned by @powerfleet/aura_standalone.ui; the host only needs host context loaded at startup.
        if (hostApplicationService.isEmbedded) {
          hostApplicationService.loadContext();
        }
        gainsightAnalyticsService.init();
      },
      deps: [EnvironmentService, HostApplicationService, GainsightAnalyticsService],
      multi: true
    },
    // Aura's analytics facade is the single source of event property construction;
    // resolve every GainsightAnalyticsService injection (incl. the initializer) to it
    // so `identify` runs the Aura-specific account resolution.
    { provide: GainsightAnalyticsService, useExisting: AuraAnalyticsService },
    {
      provide: BASE_PATH,
      deps: [EnvironmentService],
      useFactory: (environmentService: EnvironmentService) => { return environmentService.apiUrl; }
    },
    {
      provide: Configuration,
      useFactory: () => {
        const params: ConfigurationParameters = {
          withCredentials: true
        };
        return new Configuration(params);
      }
    },
    {
      provide: AUTH_SERVICE_TOKEN,
      useFactory: () => {
        const environmentService = inject(EnvironmentService);
        if (environmentService.isEmbedded) {
          console.debug("Using embedded authService");
          return inject(EmbeddedAuthService);
        } else {
          console.debug("Using standard authService");
          return inject(StandardAuthService);
        }
      }
    },
    // Bridge the host auth service to the package's streaming auth abstraction.
    { provide: AURA_UI_CHAT_AUTH, useExisting: AUTH_SERVICE_TOKEN },
    // The package wires its own BASE_PATH / Configuration internally from AURA_UI_BASE_PATH.
    provideAuraApi(),
    {
      provide: AURA_UI_BASE_PATH,
      deps: [EnvironmentService],
      useFactory: (environmentService: EnvironmentService) => environmentService.apiUrl
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([httpAuthInterceptor]),
    ),
    provideAuraMarkdown(),
    importProvidersFrom([ApiModule]),
    importProvidersFrom(BrowserAnimationsModule)
  ]
};
